import React, { useCallback, useEffect, useRef, useState } from "react";
import { VscDebugRestart } from "react-icons/vsc";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import DropdownFilter from "../../components/common/DropdownFilter";
import GigPlaceholder from "../../components/common/Placeholders/GigPlaceholder";
import RangeFilter from "../../components/common/RangeFilter";
import SearchFilter from "../../components/common/SearchFilter";
import useJobsSelectorByPagination from "../../store/hooks/useJobsSelectorByPagination";
import { jobsByPaginate } from "../../store/thunks/jobsThunk";
import { colors } from "../../utils/Colors";
import JobCard2, { ExternalJobCard } from "../Jobs/JobCard2";
import { apiGetExternalJobs } from "../../api/ai.api";

const SearchParamsKeys = {
  query: "query",
  sortBy: "sortBy",
  workType: "workType",
  minBudgetLimit: "min-budgetLimit",
  maxBudgetLimit: "max-budgetLimit",
  location: "location",
  section: "section",
};

const getSearchQueryFromParams = (params) => {
  return (
    params.get(SearchParamsKeys["query"]) ||
    params.get("jobs.searchText") ||
    params.get("searchText") ||
    ""
  );
};

const JobsContainer = ({ mapJobs = [], jobsLoading: loading, isJobs=false }) => {
  const scrollViewRef = useRef(null);
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
  const [searchParams, setSearchParams] = useSearchParams({
    location: "areaWise",
  });

  // Read AI-applied search text from Redux
  const aiSearchText = useSelector((state) => state.aiSearch?.filters?.searchText || "");
  const {
    data: jobsData,
    loading: jobLoading,
    hasMore,
    page,
    limit,
  } = useJobsSelectorByPagination();

  useEffect(() => {
    const canonicalQuery = getSearchQueryFromParams(searchParams);
    const currentQuery = searchParams.get(SearchParamsKeys["query"]) || "";

    if (!canonicalQuery || currentQuery === canonicalQuery) return;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(SearchParamsKeys["query"], canonicalQuery);
      return next;
    });
  }, [searchParams, setSearchParams]);

  const handleScroll = useCallback(() => {
    if (
      !hasMore ||
      searchParams.get(SearchParamsKeys["location"]) !== "global"
    ) {
      return;
    }
    const params = {
      ...{
        searchText: getSearchQueryFromParams(searchParams),
        sortBy: searchParams.get(SearchParamsKeys["sortBy"]),
        workType: searchParams.get(SearchParamsKeys["workType"]),
        minPrice: searchParams.get(SearchParamsKeys["minBudgetLimit"]),
        maxPrice: searchParams.get(SearchParamsKeys["maxBudgetLimit"]),
        section: isJobs ? "jobs" : "projects",
      },
      page: page + 1,
      limit,
    };
    if (isMobile) {
      const threshold = document.body.offsetHeight * 0.8;
      if (window.innerHeight + window.scrollY >= threshold && !loading) {
        dispatch(jobsByPaginate(params));
      }
    } else {
      if (scrollViewRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollViewRef.current;
        const threshold = scrollHeight * 0.9;
        if (scrollTop + clientHeight >= threshold && !loading) {
          dispatch(jobsByPaginate(params));
        }
      }
    }
  }, [hasMore, searchParams, page, limit, isMobile, loading, dispatch]);

  useEffect(() => {
    const params = {
      ...{
        searchText: getSearchQueryFromParams(searchParams),
        sortBy: searchParams.get(SearchParamsKeys["sortBy"]),
        workType: searchParams.get(SearchParamsKeys["workType"]),
        minPrice: searchParams.get(SearchParamsKeys["minBudgetLimit"]),
        maxPrice: searchParams.get(SearchParamsKeys["maxBudgetLimit"]),
        section: isJobs ? "jobs" : "projects",
      },
      page: 1,
      limit,
    };
    dispatch(jobsByPaginate(params));
  }, [dispatch, limit, searchParams, isJobs]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 800);
    };

    // Run the layout reads in handleScroll at most once per frame.
    let raf = null;
    const onScroll = () => {
      if (raf != null) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        handleScroll();
      });
    };

    const scrollEl = scrollViewRef.current;
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    if (scrollEl) {
      scrollEl.addEventListener("scroll", onScroll, { passive: true });
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", onScroll);
      if (raf != null) window.cancelAnimationFrame(raf);

      if (scrollEl) {
        scrollEl.removeEventListener("scroll", onScroll);
      }
    };
  }, [isMobile, handleScroll]);

  useEffect(() => {
    if (searchParams.get(SearchParamsKeys["location"]) === "global") {
      dispatch(
        jobsByPaginate({
          ...{
            searchText: getSearchQueryFromParams(searchParams),
            sortBy: searchParams.get(SearchParamsKeys["sortBy"]),
            workType: searchParams.get(SearchParamsKeys["workType"]),
            minPrice: searchParams.get(SearchParamsKeys["minBudgetLimit"]),
            maxPrice: searchParams.get(SearchParamsKeys["maxBudgetLimit"]),
            section: isJobs ? "jobs" : "projects",
          },
          page: 1,
        })
      );
    }
  }, [searchParams, dispatch, isJobs]);

  return (
    <div className="list" ref={scrollViewRef}>
      <div
        className="home-dropdown d-flex flex-column gap-2"
        style={{ maxWidth: 600, margin: "16px auto" }}
      >
        <div className="list-header">
          <h2 className="gigs-title">
            Find The{" "}
            <span>
              {isJobs ? "Jobs" : "Projects"}
              <br />
            </span>{" "}
            You Need
          </h2>
        </div>
        <div className="d-flex flex-column justify-content-center gap-2">
          <SearchFilter
            paramId={SearchParamsKeys["query"]}
            locationParamId={SearchParamsKeys["location"]}
            externalValue={aiSearchText}
            placeholder={isJobs ? "Search by job title, user" : "Search by project title, user"}
          />
          <div className="filter d-flex gap-2 flex-wrap">
            <DropdownFilter
              paramId={SearchParamsKeys["sortBy"]}
              options={SortingOptions}
            >
              {"Sort By"}
            </DropdownFilter>
            <DropdownFilter
              paramId={SearchParamsKeys["workType"]}
              options={WorkingTypeOptions}
            >
              {"Work Type"}
            </DropdownFilter>

            <RangeFilter
              maxParamId={SearchParamsKeys["maxBudgetLimit"]}
              minParamId={SearchParamsKeys["minBudgetLimit"]}
            >
              {"Budget"}
            </RangeFilter>
            <VscDebugRestart
              style={{
                cursor: "pointer",
                width: "25px",
                height: "25px",
                color: colors.color.secondary,
              }}
              onClick={(e) => {
                setSearchParams((prev) => {
                  prev.delete("query");
                  prev.delete("searchText");
                  prev.delete("jobs.searchText");
                  prev.delete("sortBy");
                  prev.delete("workType");
                  prev.delete("min-budgetLimit");
                  prev.delete("max-budgetLimit");
                  prev.set("location", "areaWise");
                  return prev;
                });
                e.stopPropagation();
              }}
            />
          </div>
        </div>
      </div>
      <StyledJobsList>
        {(searchParams.get(SearchParamsKeys["location"]) || "areaWise") ===
        "areaWise" ? (
          <>
            {(() => {
              const allJobs = [...(mapJobs || [])];
              return allJobs.length > 0 ? (
                allJobs.map((job) =>
                  job.is_external
                    ? <ExternalJobCard key={job._id} job={job} />
                    : <JobCard2 key={job._id} {...job} />
                )
              ) : loading ? (
                <GigPlaceholder placeholders={[1, 2, 3, 4, 5, 6]} />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center mt-5"
                  style={{ color: colors.color.darkGrey }}
                >
                  {isJobs ? (<p>Sorry ... No Jobs Found</p>):(<p>Sorry ... No Project Found</p>)}
                </div>
              );
            })()}
          </>
        ) : (
          <>
            {!!jobsData?.length &&
              jobsData?.map((job) =>
                job.is_external
                  ? <ExternalJobCard key={job._id} job={job} />
                  : <JobCard2 key={job._id} {...job} />
              )}
            {jobLoading ? (
              <GigPlaceholder placeholders={[1, 2, 3, 4, 5, 6]} />
            ) : (
              !jobLoading &&
              !jobsData?.length && (
                <div
                  className="d-flex align-items-center justify-content-center mt-5"
                  style={{ color: colors.color.darkGrey }}
                >
                  {isJobs ? (<p>Sorry ... No Jobs Found</p>):(<p>Sorry ... No Project Found</p>)}
                </div>
              )
            )}
          </>
        )}
      </StyledJobsList>
    </div>
  );
};

const SortingOptions = [
  {
    value: "high-low",
    label: "Price (high to low)",
  },
  {
    value: "low-high",
    label: "Price (low to high)",
  },
];

const WorkingTypeOptions = [
  { value: "on-site", label: "Onsite" },
  { value: "online", label: "Online" },
];

const LocationOptions = [
  { value: "areaWise", label: "Area Wise" },
  { value: "global", label: "Global" },
];

const StyledJobsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin: 0;
`;

const CustomDropdownStyles = styled.div`
  .search-range-dropdown {
    min-width: 160px !important;
    width: auto !important;
    white-space: nowrap;

    .dropdown-toggle {
      padding: 8px 12px;
      font-size: 14px;
    }
  }
`;

export default JobsContainer;

// ═══════════════════════════════════════════════════════════════════════════════
// ExternalJobsSection — shows aggregated third-party low-level jobs
// Usage: <ExternalJobsSection searchText="plumber" city="Houston" />
// ═══════════════════════════════════════════════════════════════════════════════
export const ExternalJobsSection = ({ searchText = "", city = "", state = "" }) => {
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  const load = async (pg = 1, reset = false) => {
    setLoading(true);
    try {
      const res = await apiGetExternalJobs({ q: searchText, city, state, page: pg, limit: LIMIT });
      const { jobs: newJobs, total } = res.data?.data || {};
      setJobs((prev) => reset ? (newJobs || []) : [...prev, ...(newJobs || [])]);
      setHasMore(pg * LIMIT < total);
    } catch (_) {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    load(1, true);
  }, [searchText, city, state]); // eslint-disable-line

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  if (!loading && jobs.length === 0) return null;

  return (
    <ExternalSection>
      <div className="ext-header">
        <h3>🔍 Low-Level Jobs Near You <span className="ext-badge">3rd Party</span></h3>
        <p>Posted by companies — click to apply directly on their website</p>
      </div>
      <div className="ext-grid">
        {jobs.map((job) => <ExternalJobCard key={job._id} job={job} />)}
        {loading && [1,2,3].map((i) => <div key={i} className="ext-placeholder" />)}
      </div>
      {hasMore && !loading && (
        <button className="load-more-btn" onClick={loadMore}>Load More External Jobs</button>
      )}
    </ExternalSection>
  );
};

const ExternalSection = styled.div`
  padding: 20px 0;

  .ext-header {
    text-align: center;
    margin-bottom: 16px;

    h3 {
      font-size: 1.1rem;
      font-weight: 700;
      color: #1a1a2e;
    }
    p {
      font-size: 0.82rem;
      color: #888;
      margin: 0;
    }
  }
  .ext-badge {
    font-size: 0.65rem;
    background: #e8f0fe;
    color: #3367d6;
    border-radius: 20px;
    padding: 2px 8px;
    margin-left: 8px;
    vertical-align: middle;
    font-weight: 700;
  }
  .ext-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
  }
  .ext-placeholder {
    width: 320px;
    height: 200px;
    border-radius: 12px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
  }
  @keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  .load-more-btn {
    display: block;
    margin: 20px auto 0;
    padding: 10px 28px;
    border: 2px solid #0f3460;
    background: transparent;
    color: #0f3460;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &:hover { background: #0f3460; color: #fff; }
  }
`;
